<template>
  <div id="scheduler" class="mt-2 @csm:mt-4 not-supports-cq:sm:mt-4 wrapper duration-500">
    <div>
      <div
        class="flex flex-col @csm:flex-row not-supports-cq:sm:flex-row items-center gap-2"
      >
        <MADateInput
          v-model="dateValue"
          :label="$t('wordsDate')"
          :disabled="disabled"
          class="w-full"
          format="yy-mm-dd"
          data-testid="schedule-date-input"
          @update:model-value="updateSchedule"
        />
        <TimeInput
          v-model="timeValue"
          :label="$t('wordsTime')"
          :disabled="!dateValue || disabled"
          class="w-full @csm:w-auto not-supports-cq:sm:w-auto"
          :disable-in-range="true"
          data-testid="schedule-time-input"
          @update:model-value="updateSchedule"
        />
      </div>
      <p v-if="dateTime && !isFutureDate" class="text-sm text-red-600">
        {{ $t("pastDateError") }}
      </p>

      <!-- Interval -->
      <IntervalInput
        v-if="!withoutFrequency"
        :value="interval.value"
        :type="interval.type"
        :disabled="disabled"
        :date-and-time="dateTime"
        :inline-inputs="inlineInputs"
        @change="setInterval"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import TimeInput from "./TimeInput.vue";
import IntervalInput from "./IntervalInput.vue";
// import CronInput from "./CronInput.vue";
import type { ScheduleTime } from "@/types/task";
import { addMinutes, setMilliseconds, setSeconds } from "date-fns";
import MADateInput from "../primevueComponents/MADateInput.vue";
// import DropDownSelect from "../shared/DropDownSelect.vue";

const props = defineProps<{
  scheduleTime?: ScheduleTime;
  expanded?: boolean;
  disabled?: boolean;
  withoutFrequency?: boolean;
  inlineInputs?: boolean;
  initialDate?: string | Date;
}>();

const emit = defineEmits<{
  (e: "set-schedule", data: ScheduleTime): void;
  (e: "update:scheduleTime", data: ScheduleTime): void;
  (e: "remove-schedule"): void;
}>();

const dateTime = ref(
  props.scheduleTime?.startDate
    ? new Date(props.scheduleTime.startDate)
    : addMinutes(new Date(), 5)
);
const dateValue = ref(new Date(dateTime.value));
const timeValue = ref(new Date(dateTime.value));

const interval = reactive<ScheduleTime>({
  value: {
    startDateTime: setSeconds(setMilliseconds(dateTime.value, 0), 0).toISOString(),
    interval: "once",
  },
  type: "cron",
});

const isFutureDate = computed(() => {
  if (!dateTime.value) return false;
  return dateTime.value > new Date();
});

function setDateTime() {
  if (!dateValue.value || (!timeValue.value && !dateValue.value)) return;
  const hour = timeValue.value?.getHours() || 0;
  const minute = timeValue.value?.getMinutes() || 0;
  const second = timeValue.value?.getSeconds() || 0;
  const ms = timeValue.value?.getMilliseconds() || 0;

  const newDate = new Date(dateValue.value);
  newDate.setHours(hour);
  newDate.setMinutes(minute);
  newDate.setSeconds(second);
  newDate.setMilliseconds(ms);

  dateTime.value = newDate;
}

function updateSchedule() {
  setDateTime();
  if (!dateTime.value) return;
  emit("set-schedule", interval);
  emit("update:scheduleTime", interval);
}

function setInterval(s: ScheduleTime) {
  if (!s || !s.value) return;
  interval.value = s.value;
  interval.type = s.type;
  updateSchedule();
}

watch(
  dateTime,
  () => {
    if (interval.value && typeof interval.value === "object" && dateTime.value) {
      interval.value = {
        ...interval.value,
        startDateTime: setSeconds(setMilliseconds(dateTime.value, 0), 0).toISOString(),
      };
    }
  },
  {
    immediate: true,
  }
);

onBeforeMount(() => {
  if (!props.scheduleTime && !props.initialDate) return;
  if (props.initialDate) {
    dateTime.value = new Date(props.initialDate);
    timeValue.value = new Date(props.initialDate);
    dateValue.value = new Date(props.initialDate);
    return;
  }
  if (typeof props.scheduleTime?.value === "string") return;
  if (props.scheduleTime?.value.startDateTime) {
    const time = props.scheduleTime.value.startDateTime;
    dateTime.value = new Date(time);
    timeValue.value = new Date(time);
  }
});

onMounted(updateSchedule);
</script>

<style lang="scss">
@reference "@/assets/style.css";

#scheduler div.fixed.inset-0.z-50 {
  display: flex;
  flex-direction: column;
  justify-content: center;
  & div.sm\:hidden button.bg-white {
    background-color: transparent;
    @apply shadow-transparent border-transparent text-red-600;
  }
}
</style>
